import { useState, useMemo } from "react";

import { formatBytes } from "utils";

import { useSnackbar } from "contexts";
import {
  usePackageUpload,
  usePackageUploadCancel,
} from "hooks/api/usePackageUpload";
import { useTagProducts } from "hooks/api";
import type { TagShopProduct, TagShopProductVariant } from "hooks/api/useTagProducts";
import { isTagProduct } from "constants/tagShopProductTypes";

import {
  useTheme,
  Stack,
  Typography,
  Box,
  Chip,
  Paper,
  Alert,
  LinearProgress,
} from "@mui/material";
import { CheckCircle, File } from "lucide-react";

import { Button, Modal, FileUpload, Autocomplete, type MenuButtonItem } from "@repo/ui";
import { SegmentedTabs } from "components";
import { usePackageUploadStore } from "../../../../stores/packageUploadStore";

// Type for FileUpload component's File type
type FileUploadFile = {
  id: string;
  file: any; // FileWithPath from react-dropzone
  preview_url: string;
  isUploaded: boolean;
};

// Flattened option for the Standard mode Autocomplete
interface VariantOption {
  label: string;
  productId: string;
  product: TagShopProduct;
  variantId: string;
  image?: string;
  variant: TagShopProductVariant;
}

// Chip family options for Custom mode
const CHIP_FAMILY_OPTIONS: MenuButtonItem[] = [
  { label: "NTAG424 DNA", value: "ntag424_dna" },
  { label: "ST25TA", value: "st25ta_e" },
  { label: "ST25TV", value: "st25tv" },
  { label: "Other", value: "other" },
];

interface ImportPackageModalProps {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

const UploadProgress = ({ file, progress }) => {
  const theme = useTheme();
  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Typography variant="h6" textAlign="center">
        Uploading Package
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
      >
        <File size={24} color={theme.palette.text.secondary} />
        <Stack sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="500">
            {file.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatBytes(file.size)}
          </Typography>
        </Stack>
      </Paper>
      <Stack spacing={1}>
        <LinearProgress variant="determinate" value={progress} />
        <Typography variant="body2" textAlign="center" color="text.secondary">
          {progress}%
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        textAlign="center"
        color="text.secondary"
        sx={{ mt: 2 }}
      >
        Please keep this window open while the upload is in progress.
      </Typography>
    </Stack>
  );
};

export const ImportPackageModal = (props: ImportPackageModalProps) => {
  const { open, onClose, refetch } = props;
  const theme = useTheme();

  // Mode toggle state
  const [mode, setMode] = useState<"standard" | "custom">("standard");

  // Standard mode state
  const [selectedVariantOption, setSelectedVariantOption] =
    useState<VariantOption | null>(null);

  // Custom mode state — default to NTAG424 DNA
  const [selectedFamily, setSelectedFamily] = useState<string>("ntag424_dna");

  // Shared state
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [packageName] = useState<string>("");

  // Global store
  const { uploadProgress, setUploadProgress } = usePackageUploadStore();

  const { data: tagProductsData } = useTagProducts({ limit: 100, offset: 0 });
  // Tags only — the catalog also carries the desk reader (productType "Hardware"), which
  // is not a tag type a package's rows can be imported against.
  const tagShopProducts = (tagProductsData?.products || []).filter(isTagProduct);

  // Flatten products → variants for Standard autocomplete
  const variantOptions: VariantOption[] = useMemo(() => {
    const opts: VariantOption[] = [];
    for (const product of tagShopProducts) {
      for (const variant of product.variants ?? []) {
        opts.push({
          label: `${product.title} — ${variant.title}`,
          productId: product.id,
          product,
          variantId: variant.id,
          image: variant.images?.[0],
          variant,
        });
      }
    }
    return opts;
  }, [tagShopProducts]);

  const { open: notification } = useSnackbar();

  // API hooks
  const cancelUpload = usePackageUploadCancel();
  const packageUploadMutation = usePackageUpload({
    onProgress: (progress) => {
      setUploadProgress(progress.progress);
    },
    onSuccess: (data) => {
      notification({
        type: "success",
        title: `Package "${data.package.name}" created successfully`,
        text: `${data.statistics.successfulTags} tags imported`,
      });
      refetch();
      handleClose();
    },
    onError: (error) => {
      notification({
        type: "error",
        title: "Package upload failed",
        // VRG-607 — two problems, both now fixed. The `|| error?.message` rung
        // leaked axios's "Request failed with status code 400" (the provider
        // filters it now, but the rung should not be here at all), and the final
        // fallback "Upload failed" only repeated the title. A fallback exists to
        // say what to do when we have no specific reason to offer.
        text:
          error?.response?.data?.message ||
          "Please check the file and try again, or contact support if it continues.",
      });
      setUploadProgress(0);
    },
  });

  // Validation — differs by mode
  const isReadyToUpload = mode === "standard"
    ? Boolean(selectedVariantOption) && Boolean(selectedFile) && !packageUploadMutation.isPending
    : Boolean(selectedFamily) && Boolean(selectedFile) && !packageUploadMutation.isPending;

  // Handle file upload
  const handleFileUpload = (files: FileUploadFile[]) => {
    if (files.length > 0) {
      setSelectedFile(files[0].file);
    } else {
      setSelectedFile(null);
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      notification({
        type: "error",
        title: "Missing required information",
        text: "Please select a file",
      });
      return;
    }

    if (mode === "standard") {
      if (!selectedVariantOption) {
        notification({
          type: "error",
          title: "Missing required information",
          text: "Please select a package variant",
        });
        return;
      }

      packageUploadMutation.mutate({
        file: selectedFile,
        companyId: "",
        packageName: packageName || undefined,
        tagTypeId: selectedVariantOption.product.id || undefined,
        tagShopProductData: JSON.stringify(selectedVariantOption.product),
        variantId: selectedVariantOption.variantId,
        batchSize: 1000,
        maxRows: 10000,
      });
    } else {
      if (!selectedFamily) {
        notification({
          type: "error",
          title: "Missing required information",
          text: "Please select a chip family",
        });
        return;
      }

      // TODO(VERA-790): persist tagFamily on custom tags — tagFamily forwarded
      // to controller/use-case wiring is deferred; family is sent as a field
      // but not yet applied to individual tag rows.
      packageUploadMutation.mutate({
        file: selectedFile,
        companyId: "",
        packageName: packageName || undefined,
        tagFamily: selectedFamily,
        batchSize: 1000,
        maxRows: 10000,
      });
    }
  };

  // Handle modal close
  const handleClose = () => {
    // Cancel any ongoing uploads
    if (packageUploadMutation.isPending) {
      cancelUpload();
    }

    // Reset all state
    setMode("standard");
    setSelectedVariantOption(null);
    setSelectedFamily("ntag424_dna");
    setSelectedFile(null);
    setUploadProgress(0);
    packageUploadMutation.reset();

    // Close modal
    onClose();
  };

  // Render the success view
  if (packageUploadMutation.isSuccess) {
    const response = packageUploadMutation.data;
    const successRate = response
      ? Math.round(
          (response.statistics.successfulTags /
            response.statistics.processedRows) *
            100
        )
      : 0;

    return (
      <Modal open={open} onClose={handleClose} title="Upload packages">
        <Stack spacing={3}>
          <Box textAlign="center">
            <CheckCircle size={48} color={theme.palette.success.main} />
            <Typography variant="h6" mt={2}>
              Package Created Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {response?.message}
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" mb={2}>
              Upload Summary
            </Typography>

            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Package Name:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {response?.package.name}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Successful Tags:</Typography>
                <Chip
                  label={response?.statistics.successfulTags.toLocaleString()}
                  color="success"
                  size="small"
                />
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Success Rate:</Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="success.main"
                >
                  {successRate}%
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Upload packages"
      actions={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={packageUploadMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isReadyToUpload}
            loading={packageUploadMutation.isPending}
            form="upload-tags-form"
          >
            {packageUploadMutation.isPending ? "Uploading..." : "Continue"}
          </Button>
        </>
      }
    >
      <form id="upload-tags-form" onSubmit={handleSubmit}>
        <Stack spacing={4} sx={{ p: 1 }}>
          {/* Mode toggle — shared SegmentedTabs (same control as the Theme selector) */}
          <SegmentedTabs
            fullWidth
            tabs={[
              { label: "Standard", value: "standard" },
              { label: "Custom", value: "custom" },
            ]}
            value={mode}
            onChange={(newMode) => {
              setMode(newMode as "standard" | "custom");
              // Reset mode-specific selections on switch
              setSelectedVariantOption(null);
              setSelectedFamily("ntag424_dna");
            }}
          />

          {/* Standard mode — searchable Package (variant) select, shared @repo/ui Select */}
          {mode === "standard" && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Package
              </Typography>
              <Autocomplete
                fullWidth
                disablePortal
                autoHighlight
                options={variantOptions}
                value={selectedVariantOption}
                onSelect={(opt: VariantOption | null) => setSelectedVariantOption(opt)}
                placeholder="Select a package"
                getOptionLabel={(opt: VariantOption) => opt?.label ?? ""}
                isOptionEqualToValue={(a: VariantOption, b: VariantOption) =>
                  a?.variantId === b?.variantId
                }
                renderOption={(opt: VariantOption) => (
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    sx={{ py: 0.5, minWidth: 0, width: "100%" }}
                  >
                    {opt.image ? (
                      <Box
                        component="img"
                        src={opt.image}
                        alt={opt.label}
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: "contain",
                          borderRadius: "6px",
                          border: `1px solid ${theme.palette.grey[200]}`,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "6px",
                          border: `1px solid ${theme.palette.grey[200]}`,
                          backgroundColor: theme.palette.grey[50],
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body3" noWrap>
                        {opt.variant?.title ?? opt.product?.title}
                      </Typography>
                      <Typography variant="body4" color="text.secondary" noWrap>
                        {opt.variant?.sku}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              />
            </Box>
          )}

          {/* Custom mode — tag type, same shared @repo/ui Autocomplete as Package */}
          {mode === "custom" && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Tag Type
              </Typography>
              <Autocomplete
                fullWidth
                disablePortal
                autoHighlight
                options={CHIP_FAMILY_OPTIONS}
                value={CHIP_FAMILY_OPTIONS.find((o) => o.value === selectedFamily) ?? null}
                onSelect={(opt: MenuButtonItem | null) =>
                  setSelectedFamily((opt?.value as string) ?? "")
                }
                placeholder="Select a tag type"
                getOptionLabel={(opt: MenuButtonItem) => opt?.label ?? ""}
                isOptionEqualToValue={(a: MenuButtonItem, b: MenuButtonItem) => a?.value === b?.value}
                renderOption={(opt: MenuButtonItem) => (
                  <Typography variant="body3">{opt.label}</Typography>
                )}
              />
            </Box>
          )}

          {/* File Upload Zone */}
          <FileUpload
            label="Upload package file"
            description="Upload your CSV or JSON file containing tag data (Max file size: 50MB)"
            onFilesChange={handleFileUpload}
            acceptFiles={{
              "text/csv": [".csv"],
              "application/json": [".json"],
              "text/plain": [".csv"],
            }}
            showMaxFileSize={false}
            disabled={packageUploadMutation.isPending}
          />

          {/* Upload Progress Bar */}
          {packageUploadMutation.isPending && selectedFile && (
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={2}>
                <File size={20} color={theme.palette.text.secondary} />
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(selectedFile.size)}
                  </Typography>
                </Stack>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {uploadProgress}% - Please keep this window open while the upload is in progress.
              </Typography>
            </Stack>
          )}
        </Stack>
      </form>
    </Modal>
  );
};
